import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { AnyObject } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { HcpRequestStateUI } from '.';
import {
  CaseTransaction,
  CaseMileStone,
  useCaseDetailsLazyQuery,
  useCaseDetailsQuery,
  useCaseRecordDocumentsQuery,
  useServiceRequestInfoQuery
} from '../graphql';
import { AppRootState, authOrgDetailsSelector, getUnreadMessageCount } from '../redux';
import { extractCaseUrl, UNREAD_MESSAGE, clientApplicationNameSelector } from '../utils';
import './CaseDetailMainSectionTabs.scss';
import { OverviewPrescriptionDetails } from '../views/OverviewPrescriptionDetails';
import { BISection } from './OverviewBISection';
import { PrescriptionIntakesection } from './PrescriptionIntakeSection';

interface CaseDetailMainSectionTabsParams {
  caseId: string;
  transactionId: string;
}

interface CaseDetailMainSectionTabsParamsInterface {
  updateContent: number;
}

interface DocumentProps {
  __typename?: string | null;
  caseRecordId?: number | null;
  caseTransactionId?: number | null;
  created: string;
  createdBy?: string | null;
  documentId?: number | null;
  documentType?: string | null;
  entityId?: number | null;
  entityName?: string | null;
  fileName?: string | null;
  fileStorageId?: number | null;
  formComplete?: string | null;
  lastUpdated?: string | null;
  product?: string | null;
  programId?: number | null;
  signatureRequired?: boolean | null;
  source?: string | null;
  status?: string | null;
  totalPages?: number | null;
}

const summaryEventKey = '#caseDetail';
const messagesEventKeyTab = '#caseDetailMessages';
const documentsEventKeyTab = '#caseDetailDocuments';
const piEventKey = '#patientIntake';
const biEventKey = '#bi';
const paEventKey = '#pa';
const appealEventKey = '#appeal';
const enrollmentKeyTab = '#enrollment';
const availableTabs = [
  summaryEventKey,
  piEventKey,
  messagesEventKeyTab,
  biEventKey,
  documentsEventKeyTab,
  paEventKey,
  enrollmentKeyTab,
  appealEventKey
];

export const OverviewMainSectionsAll = (props: CaseDetailMainSectionTabsParamsInterface) => {
  const [updateContent, setUpdateContent] = useState(0);
  useEffect(() => {
    setUpdateContent(updateContent + 1);
  }, [props.updateContent]);
  const history = useHistory();
  const { caseId } = useParams<CaseDetailMainSectionTabsParams>();
  const currentUser = useSelector((state: AppRootState) => state.auth?.session?.user);
  const organizationId = useSelector(authOrgDetailsSelector)?.organizationId;
  const caseRecordDocumentsResult = useCaseRecordDocumentsQuery({ variables: { caseRecordId: caseId } });
  const caseRequestDocuments = caseRecordDocumentsResult?.data?.caseRecordDocuments;
  const applicationName = useSelector(clientApplicationNameSelector);
  const caseResult = useCaseDetailsQuery({ variables: { caseRecordId: caseId } });
  const caseData = caseResult.data?.result;
  const transactions = caseData?.transactions ?? [];
  const allCaseMilestones = caseData?.mileStones
    ? [...caseData.mileStones].filter((ms): ms is CaseMileStone => ms !== null).flat()
    : [];
  const openTransactions =
    transactions?.filter(
      (element: any): element is CaseTransaction => element?.caseTransactionState === 'False' && element !== null
    ) ?? [];

  const prescriptionIntakes = (caseData?.prescriptionIntakes ?? [])[0];
  const patientEnrollment = (caseData?.patientEnrollments ?? [])[0];
  const orgDetail = useSelector((state: AppRootState) => state.auth?.session?.orgDetails);
  const practiceId = orgDetail && orgDetail?.organizationId ? String(orgDetail?.organizationId) : '';
  const onTabSelect = (eventKey: string | null) => {
    if (!eventKey) return;
    if (messagesEventKeyTab === eventKey) {
      setUpdateContent(updateContent + 1);
    }

    history.push(eventKey);
  };

  const PiActive = caseData?.prescriptionIntakes ?? [];
  const BiActive = (caseData?.benefitInvestigations ?? [])[0];
  const PaActive = (caseData?.priorAuthorizations ?? [])[0];
  const AppealActive = (caseData?.appeals ?? [])[0];

  const generateCaseDocumentType = () => {
    const documentArray: DocumentProps[] = [];
    caseRequestDocuments?.map((d) => {
      documentArray.push({
        __typename: d?.__typename ?? null,
        caseRecordId: d?.caseRecordId ?? null,
        caseTransactionId: d?.caseTransactionId ?? null,
        created: d?.created ?? new Date().toLocaleString() + '',
        createdBy: 'HCP Portal',
        documentId: d?.documentId ?? null,
        documentType: d?.documentType ?? null,
        entityId: d?.entityId ?? null,
        entityName: d?.entityName ?? null,
        fileName: d?.fileName ?? null,
        fileStorageId: d?.fileStorageId ?? null,
        formComplete: d?.formComplete ?? null,
        lastUpdated: d?.lastUpdated ?? null,
        product: null, //not called in CaseRecordDocumentsQuery
        programId: null,
        signatureRequired: d?.signatureRequired ?? null,
        source: d?.source ?? null,
        status: null,
        totalPages: null
      });
    });
    return documentArray;
  };

  const documents = generateCaseDocumentType()
    .sort((a, b) => a.created.localeCompare(b.created))
    ?.reverse();
  const hashValue: string | undefined = extractCaseUrl(useLocation())?.hashValue;
  const defaultActiveKey = hashValue ? getOverviewHashValue(hashValue) : summaryEventKey;
  const transactionType = 'Prior Authorization - Pharmacy ePA';
  const biTransactionType = 'Benefit Verification - Pharmacy';
  const [execQuery, result] = useCaseDetailsLazyQuery();

  useEffect(() => {
    if (!caseId || !currentUser) return;

    execQuery({
      variables: {
        caseRecordId: '5'
      }
    });
  }, [execQuery, currentUser, caseId]);

  const caseDetails = result?.data?.result;
  const tansactionId = getOverviewTransactionId(defaultActiveKey, caseDetails ?? []);
  const dispatch = useDispatch();
  const getUnreadCaseMessageCount = useCallback(() => dispatch(getUnreadMessageCount(caseId)), []);
  useEffect(() => {
    getUnreadCaseMessageCount();
  }, [props.updateContent]);

  return (
    <div className='cmx__case-detail-main-section-tabs m-4'>
      {/* <div className='d-none'>
        {patientEnrollment?.patientEnrollmentGuid?.toString() && (
          <EnrollmentModuleResult
            enrollmentId={patientEnrollment?.patientEnrollmentGuid?.toString() || ''}
            practiceId={practiceId || ''}
            updateContent={updateContent}
          />
        )}
      </div> */}
      <HcpRequestStateUI
        errorUIEnabled
        isError={result.error}
        isLoading={result.loading}
        loadingUIEnabled
        reload={result.refetch}
        reloadingEnabled>
        <>
          <hr />
          <div className='ms-2'>
            <i className='fa-light fa-circle-arrow-up mb-3' style={{ color: '#0084A9' }}></i>
            <span className='ms-2 detail__appname text-secondary'>
              {applicationName == 'DemoTechnicaHCPPortal' ? 'Generica' : applicationName}
            </span>
          </div>
          {applicationName === 'DemoTechnicaHCPPortal' && (
            <div className='cmx__case-detail-main-section-tabs__tab-content mb-3'>
              <BISection benefitInvestigations={caseDetails?.benefitInvestigations} orgnizationId={organizationId} />
            </div>
          )}
          {
            // <Tab eventKey={piEventKey} title='PRESCRIPTION INTAKE'>

            <div className='cmx__case-detail-main-section-tabs__tab-content'>
              <OverviewPrescriptionDetails
                updateContent={updateContent}
                prescriptionID={prescriptionIntakes?.prescriptionId?.toString() || ''}
                applicationName={applicationName}>
                <PrescriptionIntakesection prescriptionIntakes={prescriptionIntakes} />
              </OverviewPrescriptionDetails>
            </div>
          }
          {/* <div className='cmx__case-detail-main-section-tabs__tab-content mt-3'>
            <Card>
              <Card.Body className='detail__appname text-secondary'>
                <i className='fa-light fa-circle-arrow-down' style={{ color: '#0084A9' }}></i>
                {prescriptionIntakes?.drugName}
              </Card.Body>
            </Card>
          </div> */}
        </>
      </HcpRequestStateUI>
    </div>
  );
};

const MessageTabContent = () => {
  const { unreadMessagesCount } = useSelector((state: AppRootState) => state.messages);

  return (
    <div className='cmtx__message-tab'>
      <span className='cmtx__message-tab__text'>MESSAGES</span>
      <OverlayTrigger placement='top' overlay={<Tooltip id='2'>{UNREAD_MESSAGE}</Tooltip>}>
        <Badge pill bg='warning' className='message-badge'>
          {unreadMessagesCount}
        </Badge>
      </OverlayTrigger>
    </div>
  );
};

export const getOverviewTransactionId = (hash: string, caseDetails: AnyObject) => {
  switch (hash) {
    case '#bi':
      if (caseDetails?.benefitInvestigations?.[0]?.bIType === 'Pharmacy') {
        return caseDetails?.benefitInvestigations?.[0]?.eBVTransactionId ?? null;
      } else {
        return caseDetails?.benefitInvestigations?.[1]?.eBVTransactionId ?? null;
      }
    case '#bi-Medical':
      if (caseDetails?.benefitInvestigations?.[0]?.bIType === 'Medical') {
        return caseDetails?.benefitInvestigations?.[0]?.eBVTransactionId ?? null;
      } else {
        return caseDetails?.benefitInvestigations?.[1]?.eBVTransactionId ?? null;
      }
    case '#bi-Pharmacy':
      if (caseDetails?.benefitInvestigations?.[0]?.bIType === 'Pharmacy') {
        return caseDetails?.benefitInvestigations?.[0]?.eBVTransactionId ?? null;
      } else {
        return caseDetails?.benefitInvestigations?.[1]?.eBVTransactionId ?? null;
      }
    case '#pa':
      if (caseDetails?.priorAuthorizations?.[0]?.pAType === 'Pharmacy PA') {
        return caseDetails?.priorAuthorizations?.[0]?.ePATransactionID ?? null;
      } else {
        return caseDetails?.priorAuthorizations?.[1]?.ePATransactionID ?? null;
      }
    case '#pa-Medical PA':
      if (caseDetails?.priorAuthorizations?.[0]?.pAType === 'Medical PA') {
        return caseDetails?.priorAuthorizations?.[0]?.ePATransactionID ?? null;
      } else {
        return caseDetails?.priorAuthorizations?.[1]?.ePATransactionID ?? null;
      }
    case '#pa-Pharmacy PA':
      if (caseDetails?.priorAuthorizations?.[0]?.pAType === 'Pharmacy PA') {
        return caseDetails?.priorAuthorizations?.[0]?.ePATransactionID ?? null;
      } else {
        return caseDetails?.priorAuthorizations?.[1]?.ePATransactionID ?? null;
      }
    case '#appeal':
      if (caseDetails?.benefitInvestigations?.[0]?.bIType === 'Pharmacy') {
        return caseDetails?.benefitInvestigations?.[0]?.eBVTransactionId ?? null;
      } else {
        return caseDetails?.benefitInvestigations?.[1]?.eBVTransactionId ?? null;
      }
    default:
      return null;
  }
};

export const getOverviewHashValue = (hash: string) => {
  switch (hash) {
    case '#patientIntake':
      return '#patientIntake';
    case '#bi':
      return '#bi';
    case '#bi-Medical':
      return '#bi';
    case '#bi-Pharmacy':
      return '#bi';
    case '#pa':
      return '#pa';
    case '#pa-Medical PA':
      return '#pa';
    case '#pa-Pharmacy PA':
      return '#pa';
    case '#appeal':
      return '#appeal';
    default:
      return hash;
  }
};
